import React, { useState } from 'react';
import { User, LogIn } from 'lucide-react';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Modal } from '../../../components/Modal';
import type { ManualMember } from '../../../types';

interface JoinTabModalProps {
    isOpen: boolean;
    tabName: string;
    onJoinAsGuest: (name: string) => Promise<void>;
    onClaimGuest: (id: string) => void;
    onLogin: () => void;
    manualMembers: ManualMember[];
    onClose: () => void;
}

export const JoinTabModal: React.FC<JoinTabModalProps> = ({
    isOpen,
    tabName,
    onJoinAsGuest,
    onClaimGuest,
    onLogin,
    manualMembers,
    onClose,
}) => {
    const [name, setName] = useState('');
    const [view, setView] = useState<'welcome' | 'join_new' | 'recover'>('welcome');

    const handleJoinSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!name.trim()) return;
        await onJoinAsGuest(name);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`Join "${tabName}"`}
        >
            <div className="space-y-6">
                {view === 'welcome' && (
                    <div className="space-y-4">
                        <p className="text-sm text-text-muted text-center px-4">
                            You've been invited to this tab!
                        </p>

                        <div className="space-y-3 pt-2">
                            <Button
                                className="w-full h-12 text-base font-bold bg-primary-600 hover:bg-primary-700 text-white shadow-md"
                                onClick={() => { setView('join_new'); }}
                            >
                                Join as Guest
                            </Button>

                            {manualMembers.length > 0 && (
                                <Button
                                    variant="outline"
                                    className="w-full h-12 text-text-body border-border-default hover:bg-bg-card-alt"
                                    onClick={() => { setView('recover'); }}
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        <User size={16} className="text-text-muted" />
                                        <span>Recover Session</span>
                                    </div>
                                </Button>
                            )}

                            <div className="relative py-2">
                                <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t border-border-default opacity-50" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase font-medium tracking-wider">
                                    <span className="bg-bg-card px-2 text-text-muted">or</span>
                                </div>
                            </div>

                            <Button
                                variant="ghost"
                                className="w-full h-12 text-primary-600 hover:bg-primary-50 hover:text-primary-700"
                                onClick={onLogin}
                            >
                                <div className="flex items-center justify-center gap-2">
                                    <LogIn size={16} />
                                    <span>Log In to Account</span>
                                </div>
                            </Button>
                        </div>
                    </div>
                )}

                {view === 'join_new' && (
                    <form onSubmit={handleJoinSubmit} className="space-y-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Button variant="ghost" size="sm" onClick={() => { setView('welcome'); }} className="text-text-muted hover:text-text-heading -ml-2">
                                &larr; Back
                            </Button>
                        </div>

                        <div className="space-y-1 text-center pb-2">
                            <h3 className="font-bold text-lg text-text-heading">What's your name?</h3>
                            <Input
                                placeholder="Your Name"
                                value={name}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setName(e.target.value); }}
                                autoFocus
                                className="text-center text-lg h-12"
                            />
                        </div>

                        <Button type="submit" className="w-full h-12 mt-2" disabled={!name.trim()}>
                            Join Tab
                        </Button>
                    </form>
                )}

                {view === 'recover' && (
                    <div className="max-h-[60vh] flex flex-col">
                        <div className="flex items-center gap-2 mb-2 flex-none">
                            <Button variant="ghost" size="sm" onClick={() => { setView('welcome'); }} className="text-text-muted hover:text-text-heading -ml-2">
                                &larr; Back
                            </Button>
                        </div>

                        <div className="space-y-1 text-center pb-4 flex-none">
                            <h3 className="font-bold text-lg text-text-heading">Who are you?</h3>
                            <p className="text-xs text-text-muted">Select your profile.</p>
                        </div>

                        <div className="flex-1 overflow-y-auto space-y-2 pr-1 min-h-[200px]">
                            {manualMembers.map(m => (
                                <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => { onClaimGuest(m.id); }}
                                    className="w-full flex items-center justify-between p-3 rounded-xl border border-border-default hover:bg-bg-card-alt hover:border-primary-300 transition-all text-left group bg-bg-card"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-bold text-sm">
                                            {m.name.substring(0, 1).toUpperCase()}
                                        </div>
                                        <span className="font-bold text-text-heading">{m.name}</span>
                                    </div>
                                    <span className="text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-900/50 px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                                        Select
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};
